import React, { useState } from "react";
import "./Analyticalpage.css";
import useQualityMetrics from "../hooks/useQualityMetrics";
import { DATABASES } from "../utils/databases";
import {
  accuracyTrend,
  dailyQueries,
  deriveKpis,
  formatPct,
  pct,
} from "../utils/qualityMetrics";

const CHART_W = 320;
const CHART_H = 140;
const CHART_PAD = 16;
// Feedback rating → design-system badge tone (styles/components.css .ds-badge--*).
const RATING_TONE = { not_helpful: "danger", partial: "warning", helpful: "success" };

const badgeClass = (tone) => (tone ? `ds-badge ds-badge--${tone}` : "ds-badge");

function goldenTone(passed, total) {
  if (!total) return null;
  return passed === total ? "success" : "danger";
}

function TrendChart({ points }) {
  if (points.length === 0) return <div className="ds-empty">No accuracy runs yet</div>;
  const step = points.length > 1 ? (CHART_W - 2 * CHART_PAD) / (points.length - 1) : 0;
  const y = (rate) => CHART_H - CHART_PAD - rate * (CHART_H - 2 * CHART_PAD);
  const coords = points.map((p, i) => [CHART_PAD + i * step, y(p.passRate)]);
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="analytics-svg" role="img"
         aria-label="Accuracy trend">
      <polyline className="analytics-trend-line" fill="none" strokeWidth="2"
                points={coords.map((c) => c.join(",")).join(" ")} />
      {coords.map(([cx, cy], i) => (
        <circle key={points[i].id} className="analytics-trend-point" cx={cx} cy={cy} r="3">
          <title>{`${points[i].label}: ${formatPct(points[i].passRate)}`}</title>
        </circle>
      ))}
    </svg>
  );
}

function DailyBars({ days }) {
  if (days.length === 0) return <div className="ds-empty">No queries in this period</div>;
  const max = Math.max(...days.map((d) => d.queries), 1);
  const barW = (CHART_W - 2 * CHART_PAD) / days.length;
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="analytics-svg" role="img"
         aria-label="Queries per day">
      {days.map((d, i) => {
        const h = (d.queries / max) * (CHART_H - 2 * CHART_PAD);
        return (
          <rect key={d.day} className="analytics-bar" x={CHART_PAD + i * barW + 1}
                y={CHART_H - CHART_PAD - h} width={Math.max(barW - 2, 1)} height={h} rx="2">
            <title>{`${d.day}: ${d.queries}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

export default function AnalyticsPage() {
  const [database, setDatabase] = useState("");
  const state = useQualityMetrics(database);
  const kpis = deriveKpis(state);
  const delta = kpis.accuracyDelta;
  const cases = state.latestRun?.results || [];
  const casesPassed = cases.filter((c) => c.overall_pass).length;

  const pendingCount = kpis.pendingReview ?? 0;

  // `tone` maps to an .analytics-kpi-value--<tone> colour from the design tokens.
  const cards = [
    {
      label: "Latest Accuracy", value: formatPct(kpis.accuracy), tone: "accent",
      sub: delta === null ? "" : `${delta >= 0 ? "↑ +" : "↓ "}${pct(delta)}% vs previous run`,
      subTone: delta !== null && delta >= 0 ? "up" : "",
    },
    { label: "Helpful Feedback", value: formatPct(kpis.helpfulRate), tone: "accent",
      sub: `${state.feedback?.totals?.total ?? 0} ratings (30d)`, subTone: "" },
    { label: "Queries (30d)", value: kpis.queries ?? "–", tone: "neutral",
      sub: kpis.errorRate === null ? "" : `${formatPct(kpis.errorRate)} errors`, subTone: "" },
    { label: "Review Queue", value: kpis.pendingReview ?? "–", tone: "warning",
      sub: "pending feedback", subTone: "" },
  ];

  return (
    <div className="ds-page analytics-page">
      <div className="ds-page-header analytics-header">
        <div>
          <h2 className="ds-page-title">Analytics &amp; Quality</h2>
          <p className="ds-page-subtitle">Last 30 days</p>
        </div>
        <select className="ds-select" aria-label="Workspace" value={database}
                onChange={(e) => setDatabase(e.target.value)}>
          <option value="">All workspaces</option>
          {DATABASES.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {state.error && <div className="ds-alert ds-alert--danger" role="alert">{state.error}</div>}
      {state.loading && <div className="ds-empty">Loading…</div>}

      <div className="analytics-content">
        <div className="analytics-kpi-grid">
          {cards.map((k) => (
            <div key={k.label} className="ds-card analytics-kpi">
              <div className="ds-label">{k.label}</div>
              <div className={`ds-stat-value analytics-kpi-value--${k.tone}`}>{k.value}</div>
              <div className={`analytics-kpi-sub${k.subTone ? ` analytics-kpi-sub--${k.subTone}` : ""}`}>
                {k.sub}
              </div>
            </div>
          ))}
        </div>

        <div className="analytics-row analytics-row--wide">
          <div className="ds-card analytics-panel">
            <h3 className="ds-section-title">Accuracy Trend — last {state.runs?.length ?? 0} runs</h3>
            <div className="analytics-chart"><TrendChart points={accuracyTrend(state.runs)} /></div>
          </div>
          <div className="ds-card analytics-panel">
            <h3 className="ds-section-title">Queries per Day</h3>
            <div className="analytics-chart"><DailyBars days={dailyQueries(state.usage?.daily)} /></div>
          </div>
        </div>

        <div className="analytics-row">
          <div className="ds-card analytics-panel">
            <div className="analytics-panel-head">
              <h3 className="ds-section-title">Expert Review Queue</h3>
              <span className={badgeClass(pendingCount > 0 ? "warning" : null)}>{pendingCount} pending</span>
            </div>
            {(state.pending || []).map((item) => (
              <div key={item.id} className="analytics-list-item">
                <span className={badgeClass(RATING_TONE[item.rating])}>{item.rating}</span>
                <span className="analytics-list-text">{item.question}</span>
              </div>
            ))}
            {state.pending && state.pending.length === 0 && (
              <div className="ds-empty">Nothing waiting for review</div>
            )}
          </div>

          <div className="ds-card analytics-panel">
            <div className="analytics-panel-head">
              <h3 className="ds-section-title">Golden Tests</h3>
              <span className={badgeClass(goldenTone(casesPassed, cases.length))}>
                {cases.length ? `${casesPassed}/${cases.length} pass` : "no run"}
              </span>
            </div>
            {cases.map((c) => {
              const status = c.overall_pass ? "pass" : "fail";
              return (
                <div key={c.id} className={`analytics-gt-item analytics-gt-item--${status}`}>
                  <span className="analytics-gt-check">{c.overall_pass ? "✓" : "✕"}</span>
                  <span className="analytics-list-text">{c.question}</span>
                  <span className="ds-mono analytics-gt-ref">{c.group || c.id}</span>
                </div>
              );
            })}
            {!cases.length && <div className="ds-empty">Run a golden test to see results</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
